/* ==========================================================================
   ZEN — Product page helpers (Dawn's product-info / product-form do the commerce)
   - zen-gallery: mobile swipe counter + progress line; follows the variant's photo
   - mobile purchase bar: appears when the main button scrolls out of view
   ========================================================================== */

(() => {
  /* ------------------------------------------------------------------
     Gallery
     ------------------------------------------------------------------ */
  if (!customElements.get('zen-gallery')) {
    class ZenGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.zen-gallery__track');
        this.current = this.querySelector('[data-zen-gallery-current]');
        this.bar = this.querySelector('[data-zen-gallery-bar]');
        if (!this.track) return;

        let ticking = false;
        this.track.addEventListener(
          'scroll',
          () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
              ticking = false;
              this.updateCounter();
            });
          },
          { passive: true }
        );

        // Follow the selected variant's featured photo (published by product-info.js)
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data.sectionId !== this.dataset.section) return;
            const mediaId = data.variant?.featured_media?.id;
            if (mediaId) this.show(`${this.dataset.section}-${mediaId}`);
          });
        }
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }

      get isSlider() {
        return this.track.scrollWidth > this.track.clientWidth + 1;
      }

      updateCounter() {
        if (!this.current || !this.isSlider) return;
        const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
        this.current.textContent = index + 1;
        if (this.bar) this.bar.style.setProperty('--zen-gallery-i', index);
      }

      show(mediaId) {
        const item = this.track.querySelector(`[data-media-id="${mediaId}"]`);
        if (!item) return;
        if (this.isSlider) {
          this.track.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
        } else if (item !== this.track.firstElementChild) {
          // Desktop: bring the colour's photo to the top, as Dawn does
          this.track.prepend(item);
        }
      }
    }
    customElements.define('zen-gallery', ZenGallery);
  }

  /* ------------------------------------------------------------------
     Purchase bar + button label
     ------------------------------------------------------------------ */
  function init(root = document) {
    root.querySelectorAll('[data-zen-product]').forEach((section) => {
      if (section.dataset.zenInit) return;
      section.dataset.zenInit = 'true';

      const sectionId = section.dataset.zenProduct;
      const submit = section.querySelector(`#ProductSubmitButton-${sectionId}`);

      // Dawn rewrites the button text from window.variantStrings after each variant change
      if (submit && window.variantStrings && submit.dataset.addLabel) {
        window.variantStrings.addToCart = submit.dataset.addLabel;
      }

      const bar = section.querySelector('[data-zen-buybar]');
      const anchor =
        submit || section.querySelector('.zen-product__prelaunch') || section.querySelector('.zen-product__price');
      if (!bar || !anchor || !('IntersectionObserver' in window)) return;

      const setBar = (visible) => {
        bar.classList.toggle('is-visible', visible);
        bar.setAttribute('aria-hidden', String(!visible));
        bar.querySelectorAll('a, button').forEach((el) => (el.tabIndex = visible ? 0 : -1));
      };

      new IntersectionObserver(([entry]) => {
        // Show only once the main button has scrolled above the viewport
        setBar(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      }).observe(anchor);

      bar.querySelector('[data-zen-buybar-add]')?.addEventListener('click', () => {
        section.querySelector(`#ProductSubmitButton-${sectionId}`)?.click();
      });

      // Keep the bar price in step with the selected variant
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== sectionId) return;
          const amount = section.querySelector(`#price-${sectionId} .zen-price__amount`);
          const target = bar.querySelector('[data-zen-buybar-price]');
          if (amount && target) target.textContent = amount.textContent;
        });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
